import { isMemoryGone, toSafeMessage } from './agentClient';
import type { AgentsCatalog, MemoryItem } from './types';

export type MemoryView = {
  status: 'loading' | 'ready' | 'error';
  items: MemoryItem[];
  error: string | null;
};

export type AgentsTabState = {
  status: 'loading' | 'ready' | 'error';
  catalog: AgentsCatalog | null;
  error: string | null;
  memory: Record<string, MemoryView>;
};

export type AgentsTabAction =
  | { type: 'load' }
  | { type: 'loaded'; catalog: AgentsCatalog }
  | { type: 'load_failed'; error: unknown }
  | { type: 'memory_loaded'; agent: string; items: MemoryItem[] }
  | { type: 'memory_failed'; agent: string; error: unknown }
  | { type: 'delete'; agent: string; id: string }
  | {
      type: 'delete_failed';
      agent: string;
      item: MemoryItem;
      index: number;
      error: unknown;
    };

export const initialAgentsTabState = (): AgentsTabState => ({
  status: 'loading',
  catalog: null,
  error: null,
  memory: {},
});

const own = (s: AgentsTabState, agent: string): MemoryView =>
  Object.prototype.hasOwnProperty.call(s.memory, agent)
    ? s.memory[agent]
    : { status: 'loading', items: [], error: null };

export function agentsTabReducer(
  state: AgentsTabState,
  action: AgentsTabAction,
): AgentsTabState {
  switch (action.type) {
    case 'load':
      return { ...initialAgentsTabState() };
    case 'loaded':
      return {
        ...state,
        status: 'ready',
        catalog: action.catalog,
        error: null,
      };
    case 'load_failed':
      return { ...state, status: 'error', error: toSafeMessage(action.error) };
    case 'memory_loaded':
      return {
        ...state,
        memory: {
          ...state.memory,
          [action.agent]: { status: 'ready', items: action.items, error: null },
        },
      };
    case 'memory_failed':
      return {
        ...state,
        memory: {
          ...state.memory,
          [action.agent]: {
            status: 'error',
            items: [],
            error: toSafeMessage(action.error),
          },
        },
      };
    case 'delete': {
      const m = own(state, action.agent);
      return {
        ...state,
        memory: {
          ...state.memory,
          [action.agent]: {
            ...m,
            items: m.items.filter((i) => i.id !== action.id),
            error: null,
          },
        },
      };
    }
    case 'delete_failed': {
      const m = own(state, action.agent);
      // Already gone (say, deleted in another tab): it stays removed, no error.
      if (isMemoryGone(action.error)) {
        return {
          ...state,
          memory: { ...state.memory, [action.agent]: { ...m, error: null } },
        };
      }
      const items = [...m.items];
      items.splice(Math.min(action.index, items.length), 0, action.item);
      return {
        ...state,
        memory: {
          ...state.memory,
          [action.agent]: {
            ...m,
            items,
            error: toSafeMessage(action.error),
          },
        },
      };
    }
  }
}
